<template>
  <div class="hr-co-input" :class="{ helper, invalid }">
    <div class="hr-co-input__label-container">
      <div v-if="label" class="hr-co-input__label">
        {{ label }}
        <small class="hr-co-input__label__optional">
          {{ optional ? '(optional)' : '' }}
        </small>
      </div>
      <p v-if="helper" class="hr-co-input__helper">
        <slot name="helper"></slot>
      </p>
    </div>

    <div class="hr-co-input__input">
      <div
        class="hr-co-input__input-group"
        :class="{ prepend: $slots.prepend, append: $slots.append }"
      >
        <div v-if="$slots.prepend">
          <slot name="prepend"></slot>
        </div>

        <div class="hr-co-input__input-container">
          <input
            ref="inputRef"
            :value="state.theValue"
            :disabled="disabled"
            :type="inputConfig.type"
            :pattern="inputConfig.pattern"
            :inputmode="inputConfig.inputmode"
            :placeholder="placeholder"
            class="form-control hr-co-input__input-field"
            :class="{
              'input-sm': small,
              'input-lg': large,
              'fs-mask': masked,
              icon:
                $slots.icon ||
                state.isLoading ||
                state.isInvalid === true ||
                state.isInvalid === false,
              password: state.isPassword,
            }"
            @input="input"
            @click.stop="$emit('click')"
            @blur="blur"
            @focus="focus"
            @keyup.enter="enter"
            @keyup.exact="keyUp"
          />

          <div class="hr-co-input__input__states">
            <div
              v-if="state.isPassword"
              class="hr-co-input__icon hr-co-input__password-toggle"
              @click="passwordToggle"
            >
              {{ theType === 'password' ? 'show' : 'hide' }}
            </div>
            <div
              v-if="$slots.icon && !state.isLoading"
              class="hr-co-input__icon"
            >
              <slot name="icon"></slot>
            </div>
            <span
              v-if="
                !$slots.iconValid &&
                state.isInvalid === false &&
                !state.isLoading
              "
              class="co-input__valid"
            >
              <co-icon name="check" class="co-input__valid-icon" />
            </span>
            <div
              class="hr-co-input__icon hr-co-input__invalid-icon"
              :class="{ active: state.isInvalid === true }"
            >
              <slot name="iconInvalid"></slot>
              <co-icon
                v-if="
                  !$slots.iconInvalid &&
                  state.isInvalid === true &&
                  !state.isLoading &&
                  !disabled
                "
                class="text-danger"
                name="exclamation-circle"
              ></co-icon>
            </div>
            <div
              class="hr-co-input__icon hr-co-input__loading-icon"
              :class="{ active: state.isLoading }"
            >
              <slot name="iconLoading"></slot>
              <co-icon
                v-if="!$slots.iconLoading && state.isLoading === true"
                name="circle-notch"
              ></co-icon>
            </div>
          </div>
        </div>

        <div v-if="$slots.append">
          <slot name="append"></slot>
        </div>
      </div>
    </div>

    <div
      v-if="$slots.invalid && state.isInvalid === true"
      class="hr-co-input__invalid"
    >
      <slot name="invalid"></slot>
    </div>
  </div>
</template>

<script>
import { browser, isMobile } from '@/core/browser-detection';
import { reactive, computed, watch, nextTick, ref } from 'vue';
import { CoIcon } from '@deputy/copilot2';
import { logWebHrError } from '@/core/logger/log-wrapper';

const types = ['text', 'password', 'number', 'tel'];

export default {
  name: 'hr-input',
  components: { CoIcon },
  props: {
    type: {
      type: String,
      default: 'text',
      validator: (val) => types.includes(val),
      description: `field types (${types.toString()})`,
    },
    formatter: {
      type: Function,
      default: null,
      description: `format input inserted, this function is executed before trigger input event`,
    },
    validator: {
      type: Function,
      default: null,
      description: `validate input inserted, this function is executed before trigger input event`,
    },
    unformatter: {
      type: Function,
      default: null,
      description: `clean input inserted, this function is executed before trigger input event`,
    },
    optional: {
      type: Boolean,
      default: false,
      description: `optional tag, the hr-co-input field assume that all new fields are required if this option is false`,
    },
    large: {
      type: Boolean,
      default: false,
      description: `field size`,
    },
    max: {
      type: Number,
      default: null,
      description: `field maxlength`,
    },
    min: {
      type: Number,
      default: null,
      description: `field minlength it will trigger the invalid state if autoValidation is true`,
    },
    small: {
      type: Boolean,
      default: false,
      description: `field size`,
    },
    label: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: [String, Number],
      default: null,
    },
    value: {
      type: [String, Number, Boolean],
      default: null,
      description: `field value`,
    },
    helper: {
      type: Boolean,
      default: null,
      description: `display helper content`,
    },
    invalid: {
      type: Boolean,
      default: null,
      description: `display invalid content`,
    },
    autoValidation: {
      type: Boolean,
      default: true,
      description: `auto trigger internal valid / invalid / no state, states when user interacts`,
    },
    loading: {
      type: Boolean,
      default: null,
      description: `set the field loading state`,
    },
    disabled: {
      type: Boolean,
      default: null,
      description: `set the field disabled state`,
    },
    masked: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const state = reactive({
      theType: props.type,
      isPassword: props.type === 'password',
      theValue: props.value,
      isInvalid: props.invalid,
      isLoading: props.loading,
      showHelper: false,
      unformatted: null,
      validatorResult: null,
      browser: browser(),
    });

    const inputRef = ref(null);
    const inputConfig = computed(() => {
      if (isMobile()) {
        if (state.theType === 'number') {
          return {
            type: 'number',
            pattern: '[0-9]*',
            inputmode: 'numeric',
          };
        }

        return {
          type: state.theType,
          pattern: '',
          inputmode: 'text',
        };
      }

      return {
        type: state.theType === 'password' ? 'password' : 'text',
        pattern: '',
        inputmode: 'text',
      };
    });

    watch(
      () => props.value,
      (newVal) => {
        if (props.formatter) {
          state.theValue = props.formatter(newVal);
          return true;
        }

        state.theValue = newVal;
      }
    );

    watch(
      () => props.invalid,
      (newVal) => {
        state.isInvalid = newVal;
      }
    );

    watch(
      () => props.loading,
      (newVal) => {
        state.isLoading = newVal;
      }
    );

    function initialize() {
      if (props.value) {
        if (props.formatter) {
          state.theValue = props.formatter(props.value);
          return true;
        }

        state.theValue = props.value;
      }
    }
    initialize();

    async function test(evt) {
      if (props.formatter) {
        state.theValue = props.formatter(state.theValue);
        evt.srcElement.value = state.theValue; // prevent real dom element to get out of sync
      }

      if (props.max) {
        if (state.theValue.length > props.max) {
          state.theValue = state.theValue.slice(0, props.max);
          evt.srcElement.value = state.theValue; // prevent real dom element to get out of sync
          return false;
        }
      }

      state.unformatted = state.unformatter
        ? props.unformatter(state.theValue)
        : state.theValue;

      if (!state.theValue || !props.autoValidation) {
        setIsInvalid(null);
        return true;
      }

      state.validatorResult = null;

      if (props.validator) {
        try {
          state.validatorResult = await props.validator(state.theValue);

          if (props.autoValidation) {
            if (state.validatorResult === false) {
              setIsInvalid(true);
            } else if (
              state.validatorResult === true ||
              state.validatorResult instanceof Object
            ) {
              setIsInvalid(false);
            } else {
              setIsInvalid(null);
            }
          }
        } catch (e) {
          setIsInvalid(null);
        }
      }
    }

    function emitEvent(evt, realEvt) {
      emit(
        evt,
        state.theValue,
        {
          value: state.theValue,
          unformatted: state.unformatted,
          isInvalid: state.isInvalid,
          helper: state.validatorResult,
        },
        realEvt
      );
    }

    async function input(evt) {
      state.theValue = evt.srcElement.value;
      try {
        await test(evt);
        emitEvent('update', evt);
      } catch (e) {
        logWebHrError({
          message: 'hr-co-input input ERROR',
          source: 'hr-input',
          metadata: {
            hasSourceElementValue: !!evt.srcElement.value,
          },
          error: e,
        });
      }
    }

    async function blur(evt) {
      try {
        await test(evt);
        emitEvent('blur', evt);
      } catch (e) {
        logWebHrError({
          message: 'hr-co-input blur ERROR',
          source: 'hr-input',
          error: e,
        });
      }
    }

    function enter() {
      emit('enter', state.theValue);
    }
    function passwordToggle() {
      state.theType = state.theType === 'password' ? 'text' : 'password';
    }
    function setIsInvalid(val) {
      // if invalid state is being controled by the props
      state.isInvalid =
        props.invalid === true || props.invalid === false ? props.invalid : val;
    }
    function focus() {
      nextTick(() => inputRef.value.focus());
      emit('focus');
    }
    function keyUp(evt) {
      emit('keyup', evt, state.theValue);
    }

    return {
      state,
      inputRef,
      inputConfig,
      input,
      blur,
      enter,
      focus,
      keyUp,
      passwordToggle,
    };
  },
};
</script>

<style scoped>
input {
  width: 100%;
}

.hr-co-input__label {
  margin-bottom: 4px;
  font-weight: 600;
}

.hr-co-input__input {
  position: relative;
}

.hr-co-input {
  width: 100%;
  position: relative;
}

.hr-co-input__password-toggle {
  cursor: pointer;
  text-transform: uppercase;
  display: inline;
  padding-right: 5px;
  font-size: 20px;
}

.hr-co-input__password-toggle .DSIcon {
  font-size: 1.8em;
}

.hr-co-input__icon {
  display: block;
  float: left;
  padding-right: 5px;
  font-size: 20px;
  height: 100%;
}

.hr-co-input__loading-icon,
.hr-co-input__invalid-icon,
.hr-co-input__valid-icon {
  display: none;
}

.hr-co-input__loading-icon.active,
.hr-co-input__invalid-icon.active,
.hr-co-input__valid-icon.active {
  display: block;
}

.hr-co-input__input-container {
  position: relative;
  flex: 1;
}

.hr-co-input__input__states {
  position: absolute;
  top: 0;
  right: 10px;
  height: 100%;
}

.hr-co-input__helper {
  font-size: 10px;
}

.hr-co-input__label__optional {
  font-weight: 100;
}

.hr-co-input__input-group.prepend {
  position: relative;
  display: flex;
  align-items: flex-end;
}

.hr-co-input__input-group.append {
  position: relative;
  display: flex;
  align-items: flex-end;
}

.hr-co-input__input-group.append .hr-co-input__input-field,
.hr-co-input__input-group.prepend .hr-co-input__input-field {
  flex-grow: 1;
  flex: 20;
}

.hr-co-input__input-field.icon,
.hr-co-input__input-field.password {
  padding-right: 95px;
}

.hr-co-input__input-field.password.icon {
  padding-right: 90px;
}

.hr-co-input__icon .co-icon {
  width: 20px !important;
  height: 20px !important;
  margin-top: 15px;
}

@media only screen and (max-width: 768px) {
  .hr-co-input,
  .hr-co-input__helper,
  :deep(.hr-co-input__input-field) {
    font-size: 16px;
  }
}
</style>
